import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../axios/axiosInstance";
import { setResultModalData, showModal } from "../modal/modalSlice";
import locationData from "../../utils/locationData/locationOptions";
import { toastApiError } from "../../helper/api/toastApiError";

const emptyAddressData = () => ({
  saveName: "",
  address: "",
  addressDescription: "",
  polygonCoords: [],
  locationData: {
    city: "",
    cityId: "",
    town: "",
    townId: "",
    lat: "",
    lng: "",
    location: "",
    region: "",
    googleAddress: "",
  },
});

const initialState = {
  addressData: emptyAddressData(),
  isLocationInsidePolygon: false,
  isPolygonDirty: false,
  isLoadingGetAllRegisteredAddressList: false,
  allRegisteredAddressList: [],
  isLoadingAddRegisteredAddress: false,
  registeredAddressData: null,
  isLoadingUpdateRegisteredAddress: false,
  isLoadingDeleteRegisteredAddress: false,
};

const rejectResponse = (data, thunkApi, fallback) => {
  const message = toastApiError(data, fallback);
  return thunkApi.rejectWithValue({ message });
};

const rejectError = (error, thunkApi, fallback) => {
  const message = toastApiError(error, fallback);
  return thunkApi.rejectWithValue({
    message,
    status: error?.response?.status ?? null,
  });
};

const unwrapList = (value) => {
  const list = value?.$values ?? value;
  return Array.isArray(list) ? list : [];
};

const firstNonBlank = (...values) =>
  values.find((value) => value !== "" && value != null);

const normalizePolygonCoords = (address) => {
  const raw = unwrapList(address?.polygonPoints ?? address?.polygonCoords);
  return raw
    .map((point, index) => ({
      lat: Number(point?.lat ?? point?.latitude),
      lng: Number(point?.lng ?? point?.longitude),
      sortOrder: Number.isFinite(Number(point?.sortOrder))
        ? Number(point.sortOrder)
        : index,
    }))
    .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng))
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((point, index) => ({ ...point, sortOrder: index }));
};

export const getAllRegisteredAddressList = createAsyncThunk(
  "registeredAddress/getAllRegisteredAddressList",
  async ({ pagination, selectedIl = "", selectedIlce = "" }, thunkApi) => {
    try {
      const { data } = await axiosInstance.get("/Load/GetKayitliAdresler", {
        params: { sayi: pagination, Il: selectedIl, Ilce: selectedIlce },
      });

      if (data?.statusCode !== 200) {
        return rejectResponse(data, thunkApi, "Kayıtlı adresler alınamadı.");
      }

      return data;
    } catch (error) {
      return rejectError(error, thunkApi, "Kayıtlı adresler alınamadı.");
    }
  }
);

export const addRegisteredAddress = createAsyncThunk(
  "registeredAddress/addRegisteredAddress",
  async (address, thunkApi) => {
    try {
      const { data } = await axiosInstance.post("/Load/AddKayitliAdress", address);

      if (data?.statusCode !== 200) {
        return rejectResponse(data, thunkApi, "Adres kaydı eklenemedi.");
      }

      thunkApi.dispatch(clearAddressData());
      thunkApi.dispatch(
        getAllRegisteredAddressList({
          pagination: 1,
          selectedIl: "",
          selectedIlce: "",
        })
      );
      thunkApi.dispatch(showModal("result"));
      thunkApi.dispatch(
        setResultModalData({
          msg: "Adres kaydı başarıyla eklendi.",
          resultType: "success",
          btnText1: "Listeyi Görüntüle",
        })
      );
      return data;
    } catch (error) {
      return rejectError(error, thunkApi, "Adres kaydı eklenemedi.");
    }
  }
);

export const updateRegisteredAddress = createAsyncThunk(
  "registeredAddress/updateRegisteredAddress",
  async (address, thunkApi) => {
    try {
      const { data } = await axiosInstance.post("/Load/UpdateKayitliAdress", address);

      if (data?.statusCode !== 200) {
        return rejectResponse(data, thunkApi, "Kayıtlı adres güncellenemedi.");
      }

      thunkApi.dispatch(
        getAllRegisteredAddressList({
          pagination: 1,
          selectedIl: "",
          selectedIlce: "",
        })
      );
      thunkApi.dispatch(showModal("result"));
      thunkApi.dispatch(
        setResultModalData({
          msg: "Kayıtlı adres başarıyla güncellendi.",
          resultType: "success",
          btnText1: "Listeyi Görüntüle",
        })
      );
      return data?.data ?? address;
    } catch (error) {
      return rejectError(error, thunkApi, "Kayıtlı adres güncellenemedi.");
    }
  }
);

export const deleteRegisteredAddress = createAsyncThunk(
  "registeredAddress/deleteRegisteredAddress",
  async (body, thunkApi) => {
    try {
      const { data } = await axiosInstance.post("/Load/DeleteKayitliAdresi", body);

      if (data?.statusCode !== 200) {
        return rejectResponse(data, thunkApi, "Kayıtlı adres silinemedi.");
      }

      thunkApi.dispatch(
        getAllRegisteredAddressList({
          pagination: 1,
          selectedIl: "",
          selectedIlce: "",
        })
      );
      thunkApi.dispatch(showModal("result"));
      thunkApi.dispatch(
        setResultModalData({
          msg: "Kayıtlı adres başarıyla silindi.",
          resultType: "success",
          btnText1: "Listeyi Görüntüle",
        })
      );
      return data;
    } catch (error) {
      return rejectError(error, thunkApi, "Kayıtlı adres silinemedi.");
    }
  }
);

const registeredAddressSlice = createSlice({
  name: "registeredAddress",
  initialState,
  reducers: {
    updateAddressData: (state, action) => {
      state.addressData = { ...state.addressData, ...action.payload };
      if (Object.prototype.hasOwnProperty.call(action.payload ?? {}, "polygonCoords")) {
        state.isPolygonDirty = true;
      }
    },
    applyRegisteredAddressData: (state, action) => {
      const address = action.payload ?? {};
      const selectedLocation =
        locationData.find(
          (item) =>
            item.town === address.addressTown && item.city === address.addressCity
        ) ?? locationData.find((item) => item.town === address.addressTown);

      const mapLat = firstNonBlank(
        address.mapAddressLat,
        address.lat,
        address.addressLat,
        selectedLocation?.lat
      );
      const mapLng = firstNonBlank(
        address.mapAddressLong,
        address.lng,
        address.addressLong,
        selectedLocation?.lng
      );
      state.registeredAddressData = address;
      state.isPolygonDirty = false;
      state.addressData = {
        saveName: address.addressName ?? "",
        address: address.address ?? "",
        addressDescription: address.addressInfo ?? "",
        polygonCoords: normalizePolygonCoords(address),
        locationData: {
          ...(selectedLocation ?? emptyAddressData().locationData),
          googleAddress: firstNonBlank(address.mapAddressName, address.googleAddress) ?? "",
          lat: mapLat ?? "",
          lng: mapLng ?? "",
        },
      };
    },
    clearAddressData: (state) => {
      state.addressData = emptyAddressData();
      state.registeredAddressData = null;
      state.isLocationInsidePolygon = false;
      state.isPolygonDirty = false;
    },
    setIsLocationInsidePolygon: (state, action) => {
      state.isLocationInsidePolygon = Boolean(action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllRegisteredAddressList.pending, (state) => {
        state.isLoadingGetAllRegisteredAddressList = true;
      })
      .addCase(getAllRegisteredAddressList.fulfilled, (state, action) => {
        state.isLoadingGetAllRegisteredAddressList = false;
        state.allRegisteredAddressList = unwrapList(action.payload?.data);
      })
      .addCase(getAllRegisteredAddressList.rejected, (state) => {
        state.isLoadingGetAllRegisteredAddressList = false;
      })
      .addCase(addRegisteredAddress.pending, (state) => {
        state.isLoadingAddRegisteredAddress = true;
      })
      .addCase(addRegisteredAddress.fulfilled, (state, action) => {
        state.isLoadingAddRegisteredAddress = false;
        if (action.payload?.data) {
          state.allRegisteredAddressList = [
            action.payload.data,
            ...state.allRegisteredAddressList,
          ];
        }
      })
      .addCase(addRegisteredAddress.rejected, (state) => {
        state.isLoadingAddRegisteredAddress = false;
      })
      .addCase(updateRegisteredAddress.pending, (state) => {
        state.isLoadingUpdateRegisteredAddress = true;
      })
      .addCase(updateRegisteredAddress.fulfilled, (state, action) => {
        state.isLoadingUpdateRegisteredAddress = false;
        state.allRegisteredAddressList = state.allRegisteredAddressList.map((item) =>
          item.id === action.payload?.id ? { ...item, ...action.payload } : item
        );
      })
      .addCase(updateRegisteredAddress.rejected, (state) => {
        state.isLoadingUpdateRegisteredAddress = false;
      })
      .addCase(deleteRegisteredAddress.pending, (state) => {
        state.isLoadingDeleteRegisteredAddress = true;
      })
      .addCase(deleteRegisteredAddress.fulfilled, (state, action) => {
        state.isLoadingDeleteRegisteredAddress = false;
        const deletedId = action.payload?.data?.id ?? action.meta.arg?.id;
        state.allRegisteredAddressList = state.allRegisteredAddressList.filter(
          (item) => item.id !== deletedId
        );
      })
      .addCase(deleteRegisteredAddress.rejected, (state) => {
        state.isLoadingDeleteRegisteredAddress = false;
      });
  },
});

export const {
  applyRegisteredAddressData,
  clearAddressData,
  setIsLocationInsidePolygon,
  updateAddressData,
} = registeredAddressSlice.actions;

export default registeredAddressSlice.reducer;
